import { produce } from 'immer';
import {
  createContext,
  useContext,
  useEffect,
  useReducer,
  type ReactNode,
} from 'react';
import { useLoaderData } from 'react-router-dom';

import { useAuthStatePageReload } from 'src/hooks/useAuthPageReload/useAuthPageReload';
import type { CategoryThreadsResult } from 'src/mapper/threadMapper';
import type { CommunityGroupsLoader } from 'src/pages/CommunityGroups';
import {
  fetchGroup,
  type fetchGroupParams,
} from 'src/services/categoriesService';
import type { ConfigStateContext } from 'src/types';

import { useAuthStatus } from './auth';
import { useConfig } from './config';

interface GroupThreadsState {
  groupThreads: CategoryThreadsResult;
  followStatus: boolean;
}

type GroupThreadsAction =
  | {
      type: 'UPDATE_GROUP_THREADS';
      payload: CategoryThreadsResult;
    }
  | {
      type: 'UPDATE_GROUP_FOLLOW_STATUS';
      payload: boolean;
    }
  | {
      type: 'UPDATE_THREAD_LIKE_IN_GROUP';
      payload: { id: string; liked: boolean; likeCountLabel: string };
    };

const GroupThreadsContext = createContext<{
  groupThreadsState: GroupThreadsState;
  groupThreadsDispatch: React.Dispatch<GroupThreadsAction>;
} | null>(null);

const groupThreadsReducer = (
  groupThreadsState: GroupThreadsState,
  action: GroupThreadsAction,
): GroupThreadsState => {
  switch (action.type) {
    case 'UPDATE_GROUP_THREADS':
      return {
        ...groupThreadsState,
        groupThreads: action.payload,
      };
    case 'UPDATE_GROUP_FOLLOW_STATUS':
      return {
        ...groupThreadsState,
        followStatus: action.payload,
      };
    case 'UPDATE_THREAD_LIKE_IN_GROUP':
      return produce(groupThreadsState, (draft) => {
        const thread = draft.groupThreads.threads.find(
          ({ id }) => id === action.payload.id,
        );
        if (thread) {
          thread.liked = action.payload.liked;
          thread.exactSocialData.likeCountLabel = action.payload.likeCountLabel;
        }
      });
    default:
      return groupThreadsState;
  }
};

interface getGroupFollowStatusProps {
  config: ConfigStateContext;
  isAuthenticated: boolean;
  groupThreadsDispatch: React.Dispatch<GroupThreadsAction>;
  threads: CategoryThreadsResult | null;
}
const useGetGroupFollowStatus = ({
  config,
  isAuthenticated,
  groupThreadsDispatch,
  threads,
}: getGroupFollowStatusProps) =>
  useEffect(() => {
    if (!threads || !isAuthenticated) return;
    getGroupFollowStatus({
      config,
      isAuthenticated,
      groupThreadsDispatch,
      threads,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threads, isAuthenticated]);

const getGroupFollowStatus = async ({
  config,
  isAuthenticated,
  groupThreadsDispatch,
  threads,
}: getGroupFollowStatusProps) => {
  if (!threads) return;
  const groupId = threads.category.groupId;
  const param: fetchGroupParams = {
    config,
    groupId,
    hasLearningProfile: isAuthenticated,
  };
  const group = await fetchGroup(param);
  const followStatus = group?.followed || false;

  groupThreadsDispatch({
    type: 'UPDATE_GROUP_FOLLOW_STATUS',
    payload: followStatus,
  });
};

const useRefreshGroupThreads = (
  groupThreadsDispatch: React.Dispatch<GroupThreadsAction>,
  newThreads: CategoryThreadsResult,
) =>
  useEffect(() => {
    groupThreadsDispatch({
      type: 'UPDATE_GROUP_THREADS',
      payload: newThreads,
    });
  }, [newThreads, groupThreadsDispatch]);

interface GroupThreadsProviderProps {
  children: ReactNode;
  initialData: CategoryThreadsResult;
}

export const GroupThreadsProvider = ({
  children,
  initialData,
}: GroupThreadsProviderProps) => {
  const config = useConfig();
  const { authStatus } = useAuthStatus();
  const { init, isAuthenticated } = useLoaderData() as CommunityGroupsLoader;
  const [groupThreadsState, groupThreadsDispatch] = useReducer(
    groupThreadsReducer,
    {
      groupThreads: initialData,
      followStatus: false,
    },
  );
  useAuthStatePageReload(init, isAuthenticated);
  useRefreshGroupThreads(groupThreadsDispatch, initialData);
  useGetGroupFollowStatus({
    config,
    isAuthenticated: authStatus === 'authenticated',
    groupThreadsDispatch,
    threads: groupThreadsState.groupThreads,
  });

  return (
    <GroupThreadsContext.Provider
      value={{
        groupThreadsState,
        groupThreadsDispatch,
      }}
    >
      {children}
    </GroupThreadsContext.Provider>
  );
};

export const useGroupThreads = () => {
  const context = useContext(GroupThreadsContext);
  if (!context) {
    throw new Error(
      'useGroupThreads must be used within a GroupThreadsProvider',
    );
  }
  return context;
};
